/**
 * Client-side image resize/compression before upload.
 * iPhone cameras produce 12MP (~5-8MB JPEG / ~10MB base64) which blows past
 * typical ingress body-size limits (nginx default 1MB). This utility resizes
 * the longest edge to `maxSide` (default 1600px) and re-encodes to JPEG at
 * `quality` (default 0.75), then returns a `data:image/jpeg;base64,...` URL
 * typically under ~500KB — safely under any proxy limit and fast to upload.
 *
 * Preserves aspect ratio. Returns the original dataUrl if compression fails.
 */
export async function compressImageFile(file, { maxSide = 1600, quality = 0.75 } = {}) {
  if (!file) throw new Error("Fichier manquant");

  const readDataUrl = (f) =>
    new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = () => rej(new Error("Lecture du fichier impossible"));
      r.readAsDataURL(f);
    });

  const loadImage = (src) =>
    new Promise((res, rej) => {
      const img = new Image();
      img.onload = () => res(img);
      img.onerror = () => rej(new Error("Image illisible"));
      img.src = src;
    });

  const originalDataUrl = await readDataUrl(file);
  let img;
  try {
    img = await loadImage(originalDataUrl);
  } catch {
    return originalDataUrl;
  }

  const { naturalWidth: w, naturalHeight: h } = img;
  if (!w || !h) return originalDataUrl;

  const scale = Math.min(1, maxSide / Math.max(w, h));
  const tw = Math.round(w * scale);
  const th = Math.round(h * scale);

  try {
    const canvas = document.createElement("canvas");
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, tw, th);
    // toDataURL is well-supported on iOS Safari (13+)
    const jpeg = canvas.toDataURL("image/jpeg", quality);
    // Sanity: if somehow bigger than original (rare on small PNGs), keep smaller
    if (jpeg && jpeg.length < originalDataUrl.length) return jpeg;
    return originalDataUrl;
  } catch {
    return originalDataUrl;
  }
}

/** Get approximate size in KB of a base64 data URL. */
export function dataUrlSizeKB(dataUrl = "") {
  const i = dataUrl.indexOf(",");
  const payload = i >= 0 ? dataUrl.slice(i + 1) : dataUrl;
  // base64 → bytes: len * 3/4, minus padding
  const padding = (payload.match(/=+$/) || [""])[0].length;
  return Math.round((payload.length * 3) / 4 - padding) / 1024;
}
